import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Snackbar } from "@mui/material";
import MuiAlert from '@mui/material/Alert';
import { useActions } from "../../hooks/useActions";
import { useSelector } from "react-redux";
import { IErrorMessageOverrides } from "../../models/configuration/plugin/IConfiguration";
import ConfigLoad from "../../components/shared/config/ConfigLoad";
import { RootState, store } from "../../state/store";
import EncompassService from "../../services/encompassService";
import "../../assets/style.css";
import RespMessages from "../../components/configuration/messages/respMessages";
import { ConfigurationDataType, useConfigurationData } from "../../hooks/useConfigurationData";
import { ActionType } from "../../state/actions";
import { AlertSeverity } from "../../constants/AlertTypes";
import { useConfigurationSave } from "../../hooks/useConfigurationSave";
import { hasXPathConfigChanges } from "../../utils/xpathConfigChangeDetector";
import { hasPushbackConfigChanges } from "../../utils/pushbackConfigChangeDetector";
import { hasDocumentBundlingChanges, hasEventBundlingChanges } from "../../utils/bundlingConfigChangeDetector";
import { consumeGlobalSaveHadChanges } from "../../utils/globalSaveStatus";

const ConfigResponseMessages: React.FC = () => {

    const { saveConfigMessagesData } = useActions();
    const accessToken = useSelector((state: RootState) => state.appSlice.accessToken);
    const xPathData = useSelector((state: RootState) => state.xPathData.data);
    const persistedXPathData = useSelector((state: RootState) => state.xPathData.persistedData);
    const pushbackData = useSelector((state: RootState) => state.configPushbackDefaults.data);
    const persistedPushbackData = useSelector((state: RootState) => state.configPushbackDefaults.persistedData);
    const documentBundles = useSelector((state: RootState) => state.configDocumentBundles.data);
    const persistedDocumentBundles = useSelector((state: RootState) => state.configDocumentBundles.persistedData);
    const eventBundles = useSelector((state: RootState) => state.configEventBundles.data);
    const persistedEventBundles = useSelector((state: RootState) => state.configEventBundles.persistedData);
   
    // Send Validations State
    const [messages, setMessages] = useState<IErrorMessageOverrides[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [selectedConfigId, setSelectedConfigId] = useState<string>('');

    // State Confirmations
    const [alertOpen, setAlertOpen] = useState(false);
    const [saveError, setSaveError] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState<AlertSeverity>('info');
    const [saving, setSaving] = useState<boolean>(false);

    // Loading Data
    const hasFetchedData = useRef(false);
    const { data, error, loading } = useConfigurationData(ConfigurationDataType.ERROR_MESSAGE_OVERRIDES);
    const mData = useMemo(() => data, [data]);

    // Reset fetch flag on unmount so we re-initialize on next mount
    useEffect(() => {
        return () => {
            hasFetchedData.current = false;
        };
    }, []);

    useEffect(() => {
        // Initialize from Redux data when component mounts or when data loads
        if (!hasFetchedData.current && data) {
            const configId = EncompassService.getConfigId() as string;
            setSelectedConfigId(configId);          
            setMessages(mData);
            hasFetchedData.current = true;
        }
    }, [data, mData]);

     // Handling Loading Error
     useEffect(() => {
        if (error) {
            setErrorMessage(error);
        }
    }, [error]);

    useEffect(() => {
        saveState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages]);

    const updateMessages = (updatedMessages: IErrorMessageOverrides[]) => {
        setMessages(updatedMessages);
    }

    // Call this to save local session redux state
    const saveState = () => {
        try {
            const currentState = store.getState();
            const configPlugInData = currentState.configPlugInData.data;
            const updatedConfigPlugInData = {
                ...configPlugInData,
                Configuration: {
                    ...configPlugInData?.Configuration,
                    ErrorMessageOverrides: messages
                },
            };
            store.dispatch({
                type: ActionType.SAVE_CONFIG_PLUGIN_DATA_SUCCESS,
                payload: updatedConfigPlugInData
            });

        } catch (e) {
            console.log("Error Saving State: ", e);
            openAlert(`Error Saving State: ${e}`, 'error');
        }
    }

    const save = async () => {
        const globalSaved = consumeGlobalSaveHadChanges();
        try {
            setSaving(true);
            const didSave = Boolean(await saveConfigMessagesData(selectedConfigId, messages, accessToken));
            const hasXPathChanges = xPathData ? hasXPathConfigChanges(xPathData, persistedXPathData) : false;
            const hasPushbackChanges = pushbackData ? hasPushbackConfigChanges(pushbackData, persistedPushbackData) : false;
            const hasDocumentBundlingChange = documentBundles ? hasDocumentBundlingChanges(documentBundles, persistedDocumentBundles) : false;
            const hasEventBundlingChange = eventBundles ? hasEventBundlingChanges(eventBundles, persistedEventBundles) : false;
            const didSaveAny = didSave || hasXPathChanges || hasPushbackChanges || hasDocumentBundlingChange || hasEventBundlingChange || globalSaved;
            openAlert(didSaveAny ? "Successfully Saved Changes" : "No new changes to save", didSaveAny ? "success" : "info");
            setSaveError(false);
            setErrorMessage('');
        } catch (e) {
            const error = `${e}`;
            setSaveError(true);
            setErrorMessage(error);
            setSaving(false);
        } finally {
            setSaving(false);
        }
    }

    // Register save function for header buttons
    useConfigurationSave(() => save());

    // Alert Handling 
    const handleSnackBarClose = (_event: any) => { setAlertOpen(false); }
    const openAlert = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
        setAlertSeverity(severity);
        setAlertMessage(message);
        setAlertOpen(true);
    }

    return (
        <>
            <section>
                <div>
                    {(loading || saving) && <ConfigLoad loading={loading} />}   
                </div>
                <Box
                    mt={1} component="main" sx={{
                        flexGrow: 1, overflow: "auto", margin: "2px"
                    }}
                >
                    <RespMessages messages={messages}updateMessages={updateMessages} />
                </Box>
                <Snackbar
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    open={alertOpen}
                    autoHideDuration={4000}
                    onClose={handleSnackBarClose}
                >
                    <MuiAlert
                        onClose={handleSnackBarClose}
                        severity={alertSeverity}
                        sx={{ width: '100%', fontSize: '1.2rem', padding: '12px 16px' }}>
                        {alertMessage}
                    </MuiAlert>
                </Snackbar>
            </section>
        </>
    );
};

export default ConfigResponseMessages;